import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { switchMap, mergeMap, map  } from 'rxjs/operators';
import { SimDataService } from '../../services/sim-data.service';
import { ConfirmationDialogService } from '../../../../shared/services/confirmation-dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, merge, of, combineLatest, Subscription } from 'rxjs';
import { Sim, SimWithUsage } from '../../models/sim';
import { Pool } from '../../../pools/models/pool';
import { Operator } from '../../models/operator';
import { PoolDataService } from '../../../pools/services/pool-data.service';
import { Project } from '../../../project/models/project';
import { ProjectDataService } from '../../../project/services/project-data.service';
import { Train } from '../../../trains/models/train';
import { TrainDataService } from '../../../trains/services/train-data.service';

@Component({
  selector: 'app-sims-configuration',
  template: require('./sims-configuration.component.html'),
  styles: [require('./sims-configuration.component.sass')]
})
export class SimsConfigurationComponent implements OnInit, OnDestroy {


  private readonly refreshSimList$ = new BehaviorSubject(undefined);
  sims: SimWithUsage[] | null;
  pools: Pool[];
  operators: Operator[];
  trains: Train[];

  projects$: Observable<Project[]>;


  dataSubscription: Subscription;

  projectId: number;


  constructor(
      private simDataService: SimDataService,
      private poolDataService: PoolDataService,
      private projectDataService: ProjectDataService,
      private trainDataService: TrainDataService,
      private translate: TranslateService,
      @Inject('ProjectService') private projectService: any
  ) {
    this.projectId = this.projectService.getSelectedProjectId();

    this.projects$ = this.projectDataService.getAllProjects().pipe(map(projects => projects.filter(project => project.id > 0)));

    const trains$ = this.trainDataService.getTrainslist(this.projectId);

    const sims$: Observable<SimWithUsage[] | null> = this.refreshSimList$.pipe(
        switchMap(() => merge(
            of(null),
            combineLatest(this.simDataService.getAllSims(this.projectId), this.simDataService.getAllSimsUsage(this.projectId)).pipe(
                map(([sims, simsUsage]) => {
                  return sims.map(sim => {
                    const simUsage = simsUsage && simsUsage.find(usage => usage.iccid === sim.iccid);
                    return simUsage ? {
                      ...sim,
                      ...simUsage,

                    } : {
                      ...sim,
                      priority: 2
                    }
                  })
                })
            ))));

    const operators$: Observable<Operator[]> = this.simDataService.getAllOperators(this.projectId);
    const pools$ = this.poolDataService.getAllPools(this.projectId);

    this.dataSubscription = combineLatest(sims$, operators$, pools$, trains$).subscribe((
        [sims, operators, pools, trains]) => {
      this.sims = sims ? [...sims.map(sim => {
        const currentOperator: Operator | undefined = operators.find(operator => {
          return operator.code === String(sim.simConfig.operatorCode)
        });
        const currentPool: Pool | undefined = pools.find(pool => {
          return pool.id === sim.simConfig.poolId
        })
        sim.simConfig.operator = currentOperator && currentOperator.network;
        sim.simConfig.pool = currentPool && currentPool.name || this.translate.instant('I18N_SIM.UNPOOLED');

        const currentTrain: Train | undefined = trains.find(train =>  {
          return train.relativeId === sim.trainId
        });
        sim.officialId = currentTrain && currentTrain.officialId;
        sim.trainName =  currentTrain && currentTrain.name;

        sim.linkNumber = parseInt(sim.link.replace("CELL_", ""));

        return sim;
      })] : null;
      this.operators = operators? [...operators] : [];
      this.pools = pools ? [...pools] : [];
      this.trains = trains ? [...trains] : [];
    })
  }

  ngOnInit(): void {

  }

  reload = () => {
    this.refreshSimList$.next(undefined);
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    this.dataSubscription.unsubscribe();
  }
}
